import { PageType } from '@app-types/PageType'
import { type UserDataType } from '@app-types/UserDataType'
import GenericModal from '@components/Modal/Modal'
import PullHandle from '@components/shared/PullHandle/PullHandle'
import { useUserData } from '@contexts/UserDataContext'
import { getNameByPageType } from 'helpers/userData'
import { ChangeEvent, useState } from 'react'
import { FaSliders } from 'react-icons/fa6'
import DelayedStatsEntrySetting from './DelayedStatsEntrySetting'
import DuaSettings from './DuaSettings'
import KuranSettings from './KuranSettings'
import ToolbarButton from '../PaginationAndSettingsRow/ToolbarButton'
import { ReactNode } from 'react'

type SettingsModalProps = {
  type: PageType
  autoScrollDuration?: number
  setAutoScrollDuration?: (_duration: number) => void
  quickActions?: ReactNode
  /** Optional custom trigger render prop */
  trigger?: (_openModal: () => void) => ReactNode
}

const SettingsModal = ({
  type,
  autoScrollDuration,
  setAutoScrollDuration,
  quickActions,
  trigger,
}: SettingsModalProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { userData, updateUserData } = useUserData()

  const handleInputChange = (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const value = event.target.value
    const name = event.target.name as keyof UserDataType

    updateUserData(name, value)
  }

  const handleDurationChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (setAutoScrollDuration) {
      setAutoScrollDuration(Number(e.target.value))
    }
  }

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60)
    const s = seconds % 60
    return `${m}dk ${s}sn`
  }

  return (
    <>
      {trigger ? (
        trigger(() => setIsModalOpen(true))
      ) : (
        <ToolbarButton
          title="Ayarları aç"
          ariaLabel="Ayarları aç"
          onClick={() => {
            setIsModalOpen(true)
          }}
          className="umi-event--settings-icon-click"
        >
          <FaSliders />
        </ToolbarButton>
      )}
      <GenericModal
        isOpen={isModalOpen}
        variant="bottom-sheet"
        sheetHandle={
          <PullHandle
            direction="down"
            onActivate={() => setIsModalOpen(false)}
            label="Okuma ayarlarını kapatmak için aşağı çek veya dokun"
            title="Okuma ayarlarını kapat"
            className="absolute top-1 left-1/2 -translate-x-1/2"
          />
        }
        triggerCloseModal={() => {
          setIsModalOpen(false)
        }}
        id="settings"
      >
        <div className="pt-2">
          {quickActions && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3 pl-1">
                Hızlı Eylemler
              </h4>
              {quickActions}
            </div>
          )}

          <div className="flex items-center justify-between mb-5">
            <h3 className="font-bold text-xl text-main">Okuma Ayarları</h3>
          </div>

          {type === 'kuran' && (
            <KuranSettings
              {...{
                userData,
                handleInputChange,
              }}
            />
          )}
          {type !== 'kuran' && (
            <DuaSettings
              {...{
                userData,
                handleInputChange,
              }}
            />
          )}

          {autoScrollDuration !== undefined && setAutoScrollDuration && (
            <div className="flex flex-col my-4 p-4 bg-surface-subtle border border-main rounded-2xl">
              <div className="flex justify-between items-center mb-3">
                <label htmlFor="autoScrollDuration" className="font-bold">
                  Otomatik Kaydırma Hızı
                </label>
                <span className="text-sm bg-slate-200 dark:bg-slate-600 px-2 py-0.5 rounded">
                  {formatDuration(autoScrollDuration)} / sayfa
                </span>
              </div>
              <div className="text-xs mb-2 text-slate-600 dark:text-slate-300">
                <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                  {getNameByPageType(type)}
                </span>{' '}
                için bir sayfayı ne kadar sürede okuduğunuzu belirleyin.
              </div>
              <input
                id="autoScrollDuration"
                type="range"
                min="10"
                max="600"
                step="5"
                value={autoScrollDuration}
                onChange={handleDurationChange}
                className="w-full h-2 bg-slate-300 rounded-lg appearance-none cursor-pointer dark:bg-slate-600"
              />
              <div className="flex justify-between text-xs mt-2 text-muted px-1">
                <span>Hızlı (10sn)</span>
                <span>Yavaş (10dk)</span>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center my-4 py-3 border-b border-main">
            <label htmlFor="arabicFont" className="font-bold text-main">
              Arapça Yazı Tipi
            </label>
            <select
              onChange={handleInputChange}
              value={userData.arabicFontFamily}
              name="arabicFontFamily"
              id="arabicFont"
              className="bg-surface-subtle text-main px-4 py-2 border border-main rounded-xl w-36 outline-none focus:ring-2 focus:ring-brand-secondary/50 font-medium appearance-none text-center"
            >
              <option value="arab-ver-1">Versiyon 1</option>
              <option value="arab-ver-2">Versiyon 2</option>
              <option value="arab-ver-3">Versiyon 3</option>
            </select>
          </div>
          <div className="flex justify-between items-center my-4 py-3 border-b border-main">
            <label htmlFor="arabicFontAlignment" className="font-bold text-main">
              Arapça Metni
            </label>
            <div className="flex bg-surface-subtle p-1 rounded-xl border border-main">
              <button
                type="button"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  userData.arabicFontAlignment === 'right'
                    ? 'bg-main text-brand-secondary shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                    : 'text-muted hover:text-main'
                }`}
                onClick={() => updateUserData('arabicFontAlignment', 'right')}
              >
                Sağa Hizala
              </button>
              <button
                type="button"
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  userData.arabicFontAlignment === 'middle'
                    ? 'bg-main text-brand-secondary shadow-sm ring-1 ring-black/5 dark:ring-white/10'
                    : 'text-muted hover:text-main'
                }`}
                onClick={() => updateUserData('arabicFontAlignment', 'middle')}
              >
                Ortala
              </button>
            </div>
          </div>
          <DelayedStatsEntrySetting />
        </div>
      </GenericModal>
    </>
  )
}

export default SettingsModal
